import { useEffect, useRef } from 'react';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'motion/react';
import { AppCta } from '../components/AppCta';
import { LocaleSwitcher } from '../components/LocaleSwitcher';
import { RaceAtlasLogo } from '../components/RaceAtlasLogo';
import { ScatCard } from '../components/ScatCard';
import { useMessages } from '../i18n/LocaleContext';

export const Home = () => {
  const trackRef = useRef<HTMLElement>(null);
  const t = useMessages();

  // Chaos→order resolve, pinned + scrubbed: the widget sticks in view while you
  // scroll a tall track, so the page can't advance until the resolve completes.
  // The resolve starts immediately and is fully done by ~0.65, leaving the
  // ordered card held for the final stretch before the pin releases. Reduced
  // motion pins it to the finished state (track collapses to normal flow).
  const reduced = useReducedMotion();
  const progress = useMotionValue(reduced ? 1 : 0);

  // Progress is derived straight from the track's position in the viewport on
  // every scroll, rather than via useScroll — its mount-time measurement was
  // stale under hydration and left the resolve stuck partway. 0 when the track
  // top hits the viewport top, 1 once it has scrolled its full pinned travel.
  useEffect(() => {
    if (reduced) {
      progress.set(1);
      return;
    }
    const update = () => {
      const el = trackRef.current;
      if (!el) return;
      const travel = el.offsetHeight - window.innerHeight;
      const scrolled = -el.getBoundingClientRect().top;
      progress.set(travel > 0 ? Math.min(1, Math.max(0, scrolled / travel)) : 0);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [reduced, progress]);

  const orderOpacity = useTransform(progress, [0.35, 0.58], [0, 1]);
  const orderScale = useTransform(progress, [0.35, 0.65], [0.9, 1]);
  // Parallax: the resolved card drifts up as it settles (combined with its
  // -50% centring via a template).
  const orderShift = useTransform(progress, [0.1, 0.65], [34, -6]);
  const orderY = useMotionTemplate`calc(-50% + ${orderShift}px)`;

  // Hero list preview: rows reveal one at a time when the card enters view.
  const listReveal = {
    hidden: {},
    show: { transition: { staggerChildren: 0.14, delayChildren: 0.1 } },
  };
  const rowReveal = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

  return (
    <>
      {/* header */}
      <header>
        <div className="wrap hbar">
          <RaceAtlasLogo className="logo" />
          <span className="soon">{t.nav.soon}</span>
        </div>
      </header>

      {/* hero */}
      <section className="hero">
        <div className="wrap hero-grid">
          <div>
            <span className="eyebrow">{t.hero.eyebrow}</span>
            <h1>
              {t.hero.headlineLine1}
              <br />
              {t.hero.headlineLine2}
            </h1>
            <p className="lead">{t.hero.lead}</p>
            <div className="hero-cta">
              <span className="soon">{t.nav.soon}</span>
            </div>
          </div>
          <div>
            <motion.div
              className="phone"
              initial={reduced ? 'show' : 'hidden'}
              whileInView="show"
              viewport={{ once: false, amount: 0.5 }}
              variants={listReveal}
            >
              <motion.div className="row" variants={rowReveal}>
                <div className="r-main">
                  <div className="r-top">
                    <img
                      className="fav"
                      src="https://www.google.com/s2/favicons?domain=spartan.com&sz=64"
                      alt=""
                    />
                    <span className="r-name">Spartan Sardinia</span>
                  </div>
                  <div className="r-bottom">
                    <span className="r-date num">23 Aug</span>
                    <span className="dot">·</span>
                    <span>Italy</span>
                    <span className="dot">·</span>
                    <span className="ic">
                      <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12 22 2 13 22 11 13Z" />
                      </svg>
                      2h55
                    </span>
                  </div>
                </div>
                <div className="r-price">
                  <div className="p num">~€540</div>
                  <div className="conf">
                    <span className="bead high"></span> High
                  </div>
                </div>
                <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </motion.div>
              <motion.div className="row" variants={rowReveal}>
                <div className="r-main">
                  <div className="r-top">
                    <img
                      className="fav"
                      src="https://www.google.com/s2/favicons?domain=hyrox.com&sz=64"
                      alt=""
                    />
                    <span className="r-name">Hyrox Barcelona</span>
                  </div>
                  <div className="r-bottom">
                    <span className="r-date num">5 Jul</span>
                    <span className="dot">·</span>
                    <span>Spain</span>
                    <span className="dot">·</span>
                    <span className="ic">
                      <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12 22 2 13 22 11 13Z" />
                      </svg>
                      2h15
                    </span>
                  </div>
                </div>
                <div className="r-price">
                  <div className="p num">~€610</div>
                  <div className="conf">
                    <span className="bead med"></span> Med
                  </div>
                </div>
                <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </motion.div>
              <motion.div className="row" variants={rowReveal}>
                <div className="r-main">
                  <div className="r-top">
                    <img
                      className="fav"
                      src="https://www.google.com/s2/favicons?domain=spartan.com&sz=64"
                      alt=""
                    />
                    <span className="r-name">Spartan Andorra</span>
                  </div>
                  <div className="r-bottom">
                    <span className="r-date num">14 Jun</span>
                    <span className="dot">·</span>
                    <span>Andorra</span>
                    <span className="dot">·</span>
                    <span className="ic">
                      <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12 22 2 13 22 11 13Z" />
                      </svg>
                      2h40
                    </span>
                  </div>
                </div>
                <div className="r-price">
                  <div className="p num">~€780</div>
                  <div className="conf">
                    <span className="bead high"></span> High
                  </div>
                </div>
                <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* problem — pinned scrollytelling: the widget sticks while the track scrolls */}
      <section className="c2o-track" ref={trackRef}>
        <div className="c2o-pin">
          <div className="wrap grid2">
            <div className="stack-h">
              <span className="eyebrow">{t.problem.eyebrow}</span>
              <h2>{t.problem.heading}</h2>
              <p className="body">{t.problem.body1}</p>
              <p className="body">{t.problem.body2}</p>
            </div>
            <div className="c2o">
              <ScatCard
                progress={progress}
                index={0}
                position={{ top: '4px', left: '2px' }}
                rotate={-8}
              >
                <div className="bar">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="u">skyscanner.com</span>
                </div>
                <div className="b">
                  <span className="t">DUB → BCN</span>
                  <span className="p">€212</span>
                </div>
              </ScatCard>
              <ScatCard
                progress={progress}
                index={1}
                position={{ top: '0', right: '0' }}
                rotate={7}
              >
                <div className="bar">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="u">booking.com</span>
                </div>
                <div className="b">
                  <span className="t">Hotel · 2 nts</span>
                  <span className="p">€185</span>
                </div>
              </ScatCard>
              <ScatCard
                progress={progress}
                index={2}
                position={{ top: '112px', left: '14px' }}
                rotate={-4}
              >
                <div className="bar">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="u">trips_2026.xlsx</span>
                </div>
                <div className="b">
                  <span className="t">{t.problem.realCost}</span>
                  <span className="p">≈ ?</span>
                </div>
              </ScatCard>
              <ScatCard
                progress={progress}
                index={3}
                position={{ top: '152px', right: '4px' }}
                rotate={6}
              >
                <div className="bar">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="u">Race Crew</span>
                </div>
                <div className="b" style={{ color: 'var(--muted)', fontSize: '10px' }}>
                  <span>{t.problem.crewMessage}</span>
                </div>
              </ScatCard>
              <ScatCard
                progress={progress}
                index={4}
                position={{ top: '254px', left: '46px' }}
                rotate={-3}
              >
                <div className="bar">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="u">ryanair.com</span>
                </div>
                <div className="b">
                  <span className="t">{t.problem.returnLeg}</span>
                  <span className="p">€89</span>
                </div>
              </ScatCard>
              <motion.div
                className="order"
                style={{ x: '-50%', y: orderY, scale: orderScale, opacity: orderOpacity }}
              >
                <div className="o-head">
                  <img
                    className="o-fav"
                    src="https://www.google.com/s2/favicons?domain=hyrox.com&sz=64"
                    alt=""
                  />
                  <div>
                    <div className="o-src">HYROX</div>
                    <div className="o-name">Hyrox Barcelona</div>
                  </div>
                  <span className="o-date num">5 Jul</span>
                </div>
                <div className="o-rows">
                  <div className="o-row">
                    <span className="o-lbl">Entry</span>
                    <span className="num">from €129</span>
                  </div>
                  <div className="o-row">
                    <span className="o-lbl">
                      Travel
                      <span className="o-sub">Average of recent fares</span>
                    </span>
                    <span className="num">~€226</span>
                  </div>
                  <div className="o-row">
                    <span className="o-lbl">
                      Stay
                      <span className="o-sub">2 nights · Mid-range</span>
                    </span>
                    <span className="num">~€185</span>
                  </div>
                </div>
                <div className="o-est">
                  <span className="o-est-lbl">
                    Estimated trip cost
                    <span aria-hidden>·</span>
                    <span className="bead high"></span>
                    High
                  </span>
                  <span className="o-total-num num">~€540</span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* discovery hook */}
      <section className="sec" style={{ background: 'var(--surface)' }}>
        <div className="wrap">
          <span className="eyebrow">{t.discovery.eyebrow}</span>
          <h2 style={{ marginTop: '14px', maxWidth: '18ch' }}>{t.discovery.heading}</h2>
          <p className="lead" style={{ marginTop: '18px', maxWidth: '60ch' }}>
            {t.discovery.lead}
          </p>
          <div className="feat3">
            <div className="feat">
              <div className="k">{t.discovery.feat1Kicker}</div>
              <h3>{t.discovery.feat1Heading}</h3>
              <p>{t.discovery.feat1Body}</p>
            </div>
            <div className="feat">
              <div className="k">{t.discovery.feat2Kicker}</div>
              <h3>{t.discovery.feat2Heading}</h3>
              <p>{t.discovery.feat2Body}</p>
            </div>
            <div className="feat">
              <div className="k">{t.discovery.feat3Kicker}</div>
              <h3>{t.discovery.feat3Heading}</h3>
              <p>{t.discovery.feat3Body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* two jobs */}
      <section className="sec">
        <div className="wrap">
          <span className="eyebrow">{t.twoJobs.eyebrow}</span>
          <h2 style={{ marginTop: '14px', maxWidth: '22ch' }}>{t.twoJobs.heading}</h2>
          <div className="jobs">
            <div className="job">
              <div className="tag">{t.twoJobs.freeTag}</div>
              <h3>{t.twoJobs.freeHeading}</h3>
              <p>{t.twoJobs.freeBody}</p>
            </div>
            <div className="job" style={{ borderColor: 'var(--ink)' }}>
              <div className="tag">{t.twoJobs.paidTag}</div>
              <h3>{t.twoJobs.paidHeading}</h3>
              <p>{t.twoJobs.paidBody}</p>
            </div>
          </div>
        </div>
      </section>

      {/* paid payoff */}
      <section className="sec" style={{ background: 'var(--surface)' }}>
        <div className="wrap grid2">
          <div className="stack-h">
            <span className="eyebrow">{t.payoff.eyebrow}</span>
            <h2>{t.payoff.heading}</h2>
            <p className="body">{t.payoff.body1}</p>
            <p className="body">{t.payoff.body2}</p>
          </div>
          <div className="picked-card">
            <div
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: '6px',
              }}
            >
              {t.payoff.cardDay}
            </div>
            <div className="tl">
              <div className="tl-item">
                <span className="tl-node"></span>
                <div className="tl-time num">07:15</div>
                <div className="tl-label">{t.payoff.leaveHotel}</div>
                <div className="tl-sub">{t.payoff.leaveHotelSub}</div>
              </div>
              <div className="tl-item">
                <span className="tl-node"></span>
                <div className="tl-time num">07:20</div>
                <div className="tl-label">{t.payoff.coffee}</div>
                <div className="tl-sub">{t.payoff.coffeeSub}</div>
              </div>
              <div className="tl-item anchor">
                <span className="tl-node"></span>
                <div className="tl-time num">09:30</div>
                <div className="tl-label">{t.payoff.raceStart}</div>
              </div>
              <div className="tl-item">
                <span className="tl-node"></span>
                <div className="tl-time num">~12:30</div>
                <div className="tl-label">{t.payoff.postRace}</div>
                <div className="tl-sub">{t.payoff.postRaceSub}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* who */}
      <section className="sec">
        <div className="wrap">
          <span className="eyebrow">{t.who.eyebrow}</span>
          <h2 style={{ marginTop: '14px', maxWidth: '20ch' }}>{t.who.heading}</h2>
          <p className="lead" style={{ marginTop: '18px', maxWidth: '62ch' }}>
            {t.who.lead}
          </p>
        </div>
      </section>

      {/* pricing */}
      <section className="sec" style={{ background: 'var(--surface)' }}>
        <div className="wrap">
          <span className="eyebrow">{t.pricing.eyebrow}</span>
          <h2 style={{ marginTop: '14px', maxWidth: '22ch' }}>{t.pricing.heading}</h2>
          <div className="prices">
            <div className="price">
              <div className="ptier">
                <span className="t">{t.pricing.freeTier}</span>
              </div>
              <div>
                <div className="amt num">{t.pricing.freeAmount}</div>
                <div className="cad">{t.pricing.freeCadence}</div>
              </div>
              <p className="pdesc">{t.pricing.freeDesc}</p>
              <ul>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.freeItem1}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.freeItem2}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.freeItem3}
                </li>
              </ul>
            </div>
            <div className="price hot">
              <div className="ptier">
                <span className="t">{t.pricing.perTripTier}</span>
                <span className="pill-lime">{t.pricing.perTripBadge}</span>
              </div>
              <div>
                <div className="amt num">{t.pricing.perTripAmount}</div>
                <div className="cad">{t.pricing.perTripCadence}</div>
              </div>
              <p className="pdesc">{t.pricing.perTripDesc}</p>
              <ul>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.perTripItem1}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.perTripItem2}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.perTripItem3}
                </li>
              </ul>
            </div>
            <div className="price">
              <div className="ptier">
                <span className="t">{t.pricing.seasonTier}</span>
                <span className="pill-soon">{t.pricing.seasonBadge}</span>
              </div>
              <div>
                <div className="amt num">{t.pricing.seasonAmount}</div>
                <div className="cad">{t.pricing.seasonCadence}</div>
              </div>
              <p className="pdesc">{t.pricing.seasonDesc}</p>
              <ul>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.seasonItem1}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.seasonItem2}
                </li>
                <li>
                  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4 10-11" />
                  </svg>
                  {t.pricing.seasonItem3}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* final */}
      <section className="final">
        <div className="wrap">
          <h2 style={{ maxWidth: '18ch' }}>{t.final.heading}</h2>
          <p className="lead" style={{ marginTop: '16px' }}>
            {t.final.lead}
          </p>
          <div style={{ marginTop: '28px' }}>
            <AppCta />
          </div>
        </div>
      </section>

      {/* footer */}
      <footer>
        <div className="wrap fbar">
          <RaceAtlasLogo className="logo" />
          <div className="flinks">
            <span>{t.footer.privacy}</span>
            <span>{t.footer.terms}</span>
            <a href="mailto:hello@race-atlas.com">{t.footer.contact}</a>
            <LocaleSwitcher />
          </div>
        </div>
      </footer>
    </>
  );
};
